// Which rights the Rights section shows, in which group and under which name
// (ENG2-2457 / T2 and ENG2-2454 / T1, epic ENG2-2452). Pure functions over the
// model, so the grouping is data-driven and rights.tsx holds no key lists.

import {
  RightSection,
  RIGHTS_DISPLAY_ORDER,
  aiEndDateConfig,
  beneficialOwnersConfig,
  booleans,
  globalDataConfig,
  selects,
} from './restrictions.model'
import { Element } from './restrictions.types'

type RightsElement = Element & { section?: RightSection; hidden?: boolean }

const rightsElements: RightsElement[] = [
  ...booleans,
  globalDataConfig,
  beneficialOwnersConfig,
]

const inSection = (section: RightSection): RightsElement[] => {
  const order = RIGHTS_DISPLAY_ORDER[section]
  return rightsElements
    .filter((element) => element.section === section && !element.hidden)
    .sort((a, b) => {
      const ia = order.indexOf(a.key)
      const ib = order.indexOf(b.key)
      // Anything CS has not placed yet goes last, in model order.
      return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib)
    })
}

export const includedRights = (): RightsElement[] => inSection('included')

export const addonRights = (): RightsElement[] => inSection('addon')

export const isHiddenRight = (key: string): boolean =>
  rightsElements.some((element) => element.key === key && element.hidden)

// Rights that only exist once RisikaAPI sends them for the customer. Before
// RisikaAPI ENG2-2540 ships, allow_ai is missing from the customer data and the
// update endpoint rejects it (400), so the toggle must not be offered. The same
// goes for allow_sales_triggers until RisikaAPI #3184 ships (ENG2-2815).
const RIGHTS_NEEDING_CUSTOMER_DATA = ['allow_ai', 'allow_sales_triggers']

export const isRightInCustomerData = (
  key: string,
  data: Record<string, unknown> | undefined,
): boolean =>
  !RIGHTS_NEEDING_CUSTOMER_DATA.includes(key) ||
  typeof data?.[key] === 'boolean'

export const camelize = (s: string): string => {
  if (s?.length < 3) return s
  const camelCase = s.replace(/_./g, (x) => x[1].toUpperCase())
  const allButFirstLetter = camelCase.replace(/([A-Z])/g, ' $1')
  return allButFirstLetter.charAt(0).toUpperCase() + allButFirstLetter.slice(1)
}

// The display name of a right, wherever it is shown (Rights section, Restore
// Defaults): the model label, then the Global Data date labels prefixed with
// "Global Data", the Risika for AI end date under its own label (ENG2-2614),
// then a readable form of the key.
export const rightLabel = (key: string): string => {
  const element = [...rightsElements, ...selects].find((e) => e.key === key)
  if (element) return element.label

  const dates = [globalDataConfig.startDate, globalDataConfig.endDate]
  const date = dates.find((d) => d.key === key)
  if (date) return `${globalDataConfig.label} ${date.label.toLowerCase()}`

  if (key === aiEndDateConfig.key) return aiEndDateConfig.label

  return camelize(key)
}
